/**
 * hooks/use-sync.ts
 *
 * Polls GET /sync every 30 seconds. If the state hash changes since the
 * last poll, all relevant queries are invalidated so they re-fetch.
 */
import { useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../lib/api-client'
import { getAuthPayload } from '../store/auth'

type SyncData = {
  data: { hash: string; timestamp: number }
}

export const SYNC_QUERY_KEY = ['sync'] as const

export const useSync = () => {
  const queryClient = useQueryClient()
  const payload = getAuthPayload()
  const prevHash = useRef<string | null>(null)

  const query = useQuery({
    queryKey: SYNC_QUERY_KEY,
    queryFn: () => apiFetch<SyncData>('/sync'),
    enabled: !!payload?.couple_id,
    refetchInterval: 30_000,
    staleTime: 0,
  })

  useEffect(() => {
    if (!query.data) return
    const hash = query.data.data.hash

    if (prevHash.current !== null && prevHash.current !== hash) {
      // Something changed — refresh all data queries
      queryClient.invalidateQueries({ queryKey: ['notes'] })
      queryClient.invalidateQueries({ queryKey: ['milestones'] })
      queryClient.invalidateQueries({ queryKey: ['countdowns'] })
    }

    prevHash.current = hash
  }, [query.data, queryClient])

  return query
}
